import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { Business as BusinessIcon, Fingerprint as FingerprintIcon } from '@mui/icons-material';
import { GoogleLogin } from '@react-oauth/google';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { useTranslation } from 'react-i18next';
import { googleLogin, passkeyAuthOptions, passkeyAuthVerify, passkeyGoogleVerify, postJson, scheduleProactiveRefresh } from '../lib/api';
import { getDeviceFingerprint } from '../lib/device-fingerprint';
import { setTokens, getRememberPreference } from '../lib/auth';
import { getErrorMessage, useToast } from '../lib/toast';
import { useAuth } from '../contexts/AuthContext';
import type { OrgOption, Role, TokenResponse } from '../lib/types';

interface Props {
  onSuccess: () => void;
  onAdminLogin?: () => void;
}

export function LoginPage({ onSuccess, onAdminLogin }: Props) {
  const { t } = useTranslation();
  const { notifyError, notifyInfo } = useToast();
  const auth = useAuth();

  const [preAuthToken, setPreAuthTokenState] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [selectingOrgId, setSelectingOrgId] = useState<number | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  useEffect(() => {
    setPasskeyAvailable(browserSupportsWebAuthn());
  }, []);

  async function handleGoogleSuccess(credential: string): Promise<void> {
    setGoogleLoading(true);
    try {
      let result = await googleLogin(credential);

      // Sprint 2 backend dc13a7f: when the user has any active passkey,
      // backend issues a preAuthToken gated by `passkeyVerifyRequired=true`.
      // FE must complete a WebAuthn challenge before selectOrg.
      // Anti-account-sharing: stolen Google account alone cannot proceed.
      if (result.passkeyVerifyRequired) {
        const verified = await runGooglePasskeyGate(result.preAuthToken);
        if (!verified) return; // user cancelled or verify failed
        result = verified;
      }

      if (result.organizations.length === 0) {
        notifyError(t('login.noOrganizations'));
        return;
      }

      setPreAuthTokenState(result.preAuthToken);
      setOrganizations(result.organizations);

      if (result.organizations.length === 1 && result.organizations[0]) {
        await selectOrg(result.preAuthToken, result.organizations[0].id, result.organizations);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('ACCOUNT_EXISTS_LINK_REQUIRED')) {
        notifyInfo(t('login.accountExistsLinkRequired'));
      } else if (message.includes('USER_NOT_FOUND')) {
        notifyInfo(t('login.userNotFound'));
      } else if (message.includes('ACCOUNT_DEACTIVATED')) {
        notifyError(t('login.accountDeactivated'));
      } else if (message.includes('ADMIN_LOGIN_REQUIRED')) {
        notifyInfo(t('login.adminLoginRequired'));
        if (onAdminLogin) onAdminLogin();
      } else {
        notifyError(getErrorMessage(err, t('login.googleError')));
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  /** Sprint 2 backend dc13a7f: WebAuthn challenge to trade preAuthToken
   *  with `requiresPasskey=true` for one with `passkeyVerified=true`.
   *  Returns null if the user cancels the prompt or verification fails. */
  async function runGooglePasskeyGate(preAuthToken: string): Promise<{
    preAuthToken: string;
    organizations: OrgOption[];
  } | null> {
    try {
      const options = await passkeyAuthOptions();
      const credential = await startAuthentication({
        optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON
      });
      const verified = await passkeyGoogleVerify(
        preAuthToken,
        credential as unknown as Record<string, unknown>
      );
      return {
        preAuthToken: verified.preAuthToken,
        organizations: verified.organizations.map((o) => ({
          id: o.id,
          name: o.name,
          role: o.role as Role
        }))
      };
    } catch (err) {
      // NotAllowedError = user cancelled the passkey prompt.
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError') {
        notifyInfo(t('passkey.verifyCancelled'));
      } else {
        notifyError(getErrorMessage(err, t('passkey.verifyError')));
      }
      return null;
    }
  }

  async function handlePasskeyLogin(): Promise<void> {
    setPasskeyLoading(true);
    try {
      const options = await passkeyAuthOptions();
      const credential = await startAuthentication({ optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON });
      const result = await passkeyAuthVerify(credential as unknown as Record<string, unknown>);

      if (result.organizations.length === 0) {
        notifyError(t('login.noOrganizations'));
        return;
      }

      const orgs: OrgOption[] = result.organizations.map((o) => ({ id: o.id, name: o.name, role: o.role as Role }));
      setPreAuthTokenState(result.preAuthToken);
      setOrganizations(orgs);
      auth.setAvailableOrgs(orgs);
      auth.setPreAuthToken(result.preAuthToken);

      if (orgs.length === 1 && orgs[0]) {
        await selectOrg(result.preAuthToken, orgs[0].id, orgs);
      }
    } catch (err) {
      notifyError(getErrorMessage(err, t('passkey.loginError')));
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function selectOrg(token: string, orgId: number, orgs?: OrgOption[]): Promise<void> {
    setSelectingOrgId(orgId);
    try {
      // Sprint 3.4 (FE 7.51.0): device fingerprint header habilita backend
      // anti-account-sharing detection (concurrent session com fingerprint
      // diferente → revoga + email). Fingerprint failure não-bloqueante:
      // se compute falha, header omitido e backend cai em LRU eviction normal.
      const fingerprint = await getDeviceFingerprint().catch(() => '');
      const headers: Record<string, string> = fingerprint
        ? { 'X-Device-Fingerprint': fingerprint }
        : {};

      const result = await postJson<TokenResponse, { preAuthToken: string; orgId: number }>(
        '/auth/select-org',
        { preAuthToken: token, orgId },
        { auth: false, retryOnAuthError: false, headers }
      );
      setTokens(result.accessToken, result.refreshToken, getRememberPreference());
      auth.setTermsAccepted(result.termsAccepted);
      scheduleProactiveRefresh(result.expiresAt);
      auth.setAvailableOrgs(orgs ?? organizations);
      auth.setPreAuthToken(token);
      onSuccess();
    } catch (err) {
      notifyError(getErrorMessage(err, t('login.orgSelectError')));
      setPreAuthTokenState(null);
      setOrganizations([]);
    } finally {
      setSelectingOrgId(null);
    }
  }

  function handleBack(): void {
    setPreAuthTokenState(null);
    setOrganizations([]);
  }

  const showOrgSelect = preAuthToken !== null && organizations.length > 1;

  return (
    <Box
      data-testid="login-root"
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
        <Card
          sx={{
            width: '100%',
            maxWidth: showOrgSelect ? 520 : 420,
            borderRadius: 4,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            transition: 'max-width 0.3s ease'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {showOrgSelect ? (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {t('login.selectOrg')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.selectOrgSubtitle')}
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {organizations.map((org) => (
                    <Button
                      key={org.id}
                      variant="outlined"
                      onClick={() => void selectOrg(preAuthToken, org.id)}
                      disabled={selectingOrgId !== null}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        px: 2.5,
                        textAlign: 'left',
                        textTransform: 'none',
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(45, 212, 191, 0.06)'
                        }
                      }}
                    >
                      <BusinessIcon sx={{ mr: 2, color: 'text.secondary', fontSize: 28 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {org.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`common.role.${org.role}`, { defaultValue: org.role })}
                        </Typography>
                      </Box>
                      {selectingOrgId === org.id && (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                      )}
                    </Button>
                  ))}
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  onClick={handleBack}
                  sx={{ alignSelf: 'center', color: 'text.secondary' }}
                >
                  {t('login.backToLogin')}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={3} alignItems="center">
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" gutterBottom>
                    {t('login.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.subtitle')}
                  </Typography>
                </Box>

                {passkeyAvailable && (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={passkeyLoading ? <CircularProgress size={16} color="inherit" /> : <FingerprintIcon />}
                    onClick={() => void handlePasskeyLogin()}
                    disabled={passkeyLoading || googleLoading}
                    sx={{ py: 1.5 }}
                  >
                    {t('passkey.login')}
                  </Button>
                )}

                <Box sx={{ position: 'relative', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {googleLoading ? (
                    <CircularProgress size={28} />
                  ) : (
                    <GoogleLogin
                      theme="filled_black"
                      size="large"
                      text="continue_with"
                      shape="rectangular"
                      width="320"
                      onSuccess={(credentialResponse) => {
                        if (credentialResponse.credential) {
                          void handleGoogleSuccess(credentialResponse.credential);
                        }
                      }}
                      onError={() => {
                        notifyError(t('login.googleError'));
                      }}
                    />
                  )}
                </Box>

                <Divider sx={{ width: '100%' }} />

                <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
                  {/* Sprint 3.4 (FE 7.51.0): link "Perdi minha passkey" → recovery flow */}
                  <Button
                    variant="text"
                    size="small"
                    color="inherit"
                    onClick={() => { window.location.href = '/recovery/start'; }}
                    sx={{ color: 'text.disabled', fontSize: '0.75rem' }}
                  >
                    {t('passkey.recovery.lostLink')}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    color="inherit"
                    onClick={onAdminLogin}
                    sx={{ color: 'text.disabled', fontSize: '0.75rem' }}
                  >
                    {t('login.adminTitle')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ textAlign: 'center' }}
        >
          v{__APP_VERSION__}
        </Typography>
      </Stack>
    </Box>
  );
}
